/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { ApiDependenciesService } from './api-dependencies.service';
import { environment } from 'environments/environment';
import { HttpHeaders } from '@angular/common/http';
import { Constants } from 'app/core/models/constants';

export abstract class BaseApiService {
  protected readonly baseApiUrl: string = environment.baseUrl;
  protected readonly apiPrefix: string = environment.apiPrefix;
  protected _instanceEndpointCache = '';

  protected endpoint: string = null;
  protected baseApiOverride: string = null;
  protected apiPrefixOverride: string = null;
  protected includeAuthorizationHeader: boolean = true; 

  constructor(protected dependencies: ApiDependenciesService) {}

  protected buildUrl() {
    if (this.endpoint === null) {
      throw new Error('Must provide Web API endpoint for data service.');
    }
    if (this.baseApiOverride !== null && this.apiPrefixOverride !== null) {
      return this.baseApiOverride + this.apiPrefixOverride + this.endpoint;
    }
    return this.baseApiUrl + this.apiPrefix + this.endpoint;
  }

  protected buildHeaders(): HttpHeaders {
    // TODO refactor to call Cache service abstraction
    const accessTokenFromCookie = localStorage.getItem(Constants.LocalStorage.KeyAccessToken);

    if (this.includeAuthorizationHeader) {
      return new HttpHeaders({
        Accept: 'application/json',
        'X-API-Version': '1',
        Authorization: 'Bearer ' + accessTokenFromCookie,
        observe: 'response'
      });
    }

    return new HttpHeaders({
      Accept: 'application/json',
      'X-API-Version': '1',
      observe: 'response'
    });
  }

  protected handleApiError(error: any, request: any): Promise<any> {
    if (this.dependencies.cookieService.isOffline()) {
      // This should not happen as no API requests should have been made whilst offline
      this.dependencies.snackBar.error('An API request was made when offline.');

      return Promise.resolve(error.message || error);
    }

    
    if (error.status === 401) {
      // Not authenticated
      // TODO [technical debt] user custom toast template
      // TODO [technical debt] toast setting to only fire 1 of the same toast instance
      // this.dependencies.toast.error(new Toast('Your session has expired or you are not authorised to complete this action. Please <a href="/login"><strong>Login</strong></a> again.'));
      localStorage.setItem(Constants.LocalStorage.RedirectUrl, this.dependencies.router.url);
      this.clearAuthenticationState();
      this.dependencies.snackBar.error('Your session has expired or you are not authorised to complete this action. Please sign in again.');
      window.location.href = '/login?r=1';
    }
    let snackBarMessage: string;

    if (error.status === 403) {
      // Forbidden
      // TODO [technical debt] user custom toast template
      // TODO [technical debt] toast setting to only fire 1 of the same toast instance
      this.dependencies.snackBar.error('Oops! It looks like you don\'t have permission. Please contact you system administrator.');
    }

    // TODO refactor this error handling!!!
    if (error !== null && error.hasOwnProperty('error')) {
      let errors: any[] = [];
      const errorResponse = error.error;

      if (errorResponse !== null && errorResponse.hasOwnProperty('Errors') && errorResponse.Errors.length > 0) {
        errors = errorResponse.Errors;
      } else if (errorResponse !== null && errorResponse.hasOwnProperty('ValidationErrors') && errorResponse.ValidationErrors.length > 0) {
        errors = errorResponse.ValidationErrors.map(err => err.key + ': ' + err.value);
      } else if (errorResponse !== null && errorResponse.hasOwnProperty('message')) {
        errors.push(errorResponse.message);
      }

      snackBarMessage = 'Data Service Error: ' + error.status + ' - ' + error.statusText + '. Check console for details';

      this.dependencies.snackBar.error(snackBarMessage);

      return Promise.resolve(errors);
    }

    this.resetEndpointFromCache();

    if (error !== null && error.hasOwnProperty('error') && error.error.hasOwnProperty('errors') && error.error.errors.length > 0) {
      snackBarMessage = 'There has been an unexpected problem with an API request. Please check the console for details';
      this.dependencies.snackBar.error(snackBarMessage);
      const consoleMessage = error.error.errors[0] + '\n Please contact Kaleida if you continue to see this error quoting the error reference';
      console.error(consoleMessage);
       
      return Promise.resolve(consoleMessage);
    }

    return Promise.reject(error.message || error);
  }

  public getServerErrors(errorResponse: any): ApiDomainError[] {
    this.resetEndpointFromCache();
    const error = errorResponse.json() as ApiResponse;
    let errors: ApiDomainError[] = [];
    if (error !== null && error.hasOwnProperty('errors')) {
      errors = error.errors;
    }
    return errors;
  }

  public getServerValdiationErrors(errorResponse: any): ApiValidationError[] {
    this.resetEndpointFromCache();
    const error = errorResponse.json() as ApiResponse;
    let validationErrors: ApiValidationError[] = [];
    if (error !== null && error.hasOwnProperty('validationErrors')) {
      validationErrors = error.validationErrors;
    }
    return validationErrors;
  }

  protected setEndpoint(customEndpoint: string): any {
    this._instanceEndpointCache = this.endpoint;
    this.endpoint = customEndpoint;
    return this;
  }

  protected resetEndpointFromCache() {
    if (this._instanceEndpointCache && this._instanceEndpointCache.length > 0) {
      this.endpoint = this._instanceEndpointCache;
    }
    this._instanceEndpointCache = '';
  }

  private clearAuthenticationState() {
    localStorage.removeItem(Constants.LocalStorage.KeyAccessToken);
    localStorage.removeItem(Constants.LocalStorage.KeyRefreshToken);
    this.dependencies.cookieService.delete(Constants.Cookies.KeyLoggedInUserId);
    this.dependencies.cookieService.delete(Constants.Cookies.KeyLoggedInUserName);
    this.dependencies.cookieService.delete(Constants.Cookies.KeyLoggedInFullName);
    this.dependencies.cookieService.delete(Constants.Cookies.KeyLoggedInUserType);
    this.dependencies.cookieService.delete(Constants.Cookies.KeyOrganiserId);
  }
}

export class ApiResponse {
  data: any;
  success: boolean;
  errors: ApiDomainError[];
  validationErrors: ApiValidationError[];
}

export class ApiValidationError {
  key: string;
  value: string;
}

export class ApiDomainError {
  key: string;
  value: string;
}
